import type {ReactElement} from 'react';
import {useEffect, useMemo, useState} from 'react';
import {SkeletonBrick} from '@Components/skeleton-brick';
import {addFonts} from '@Libraries/font-library';
import {noop} from '@Utils/general.util';
import ClickableDiv from '@Components/clickable-div/clickable-div';
import {v4 as uuidv4} from 'uuid';
import type {StyleTileProps} from './style-tile.types';
import {STYLE_TILE_COLORS_LIMIT} from './style-tile.types';
import styles from './style-tile.module.scss';
import {Pill, PILL_CONTENT_TYPE, PILL_SIZE} from '@Components/pill-legacy';
import {TextSize} from '@Components/text';

export function StyleTile({
  imageURL,
  fontFamily,
  tileText = '',
  colorHexes = [],
  isSelected = false,
  isLoading = false,
  className = '',
  showImage = true,
  imageClassName = '',
  onClick = noop,
  showCursorPointer = true,
  hasGeneratedWithAIPill = false,
  tileTextClasName = ''
}: StyleTileProps): ReactElement<any> {
  const [isFontLoading, setIsFontLoading] = useState(true);
  const colorKeys = useMemo((): string[] => {
    return [uuidv4(), uuidv4(), uuidv4()];
  }, []);

  const isImageURLSet = (): boolean => {
    return imageURL !== '' && imageURL !== undefined && imageURL !== null;
  };

  const areColorHexesSet = (): boolean => {
    return colorHexes !== null && colorHexes !== undefined && colorHexes.length > 0;
  };

  const getAltForImage = (): string => {
    if (tileText) {
      return tileText;
    }

    return window.i18next.t('logo');
  };

  const getImageContainerContent = (): ReactElement<any> => {
    if (isLoading) {
      return <SkeletonBrick className={styles.imageLoader} />;
    }

    if (!isImageURLSet()) {
      return <div className={styles.emptyImgBox} />;
    }

    return <img className={`${styles.img} ${isLoading ? '_hidden' : ''} ${imageClassName}`} src={imageURL} alt={getAltForImage()} />;
  };

  const getColorLoaders = (): ReactElement<any>[] => {
    return Array.from({length: STYLE_TILE_COLORS_LIMIT}, (_, index): ReactElement<any> => {
      return <SkeletonBrick key={`color-loader-${index}-${tileText}`} className={`${styles.colorLoader} flex-1 radius-4`} />;
    });
  };

  const getColorsEmptyState = (): ReactElement<any>[] => {
    return Array.from({length: STYLE_TILE_COLORS_LIMIT}, (_, index): ReactElement<any> => {
      return <div key={`color-placeholder-${index}-${tileText}`} className={`${styles.emptyColor} flex-1 radius-4`} />;
    });
  };

  const getColors = (): ReactElement<any>[] => {
    const colors = (colorHexes ?? []).slice(0, STYLE_TILE_COLORS_LIMIT);
    return colors.map((color, i) => {
      return <div key={`color-${colorKeys[i]}-${color}-${tileText}`} className={`flex-1 _full-height ${styles.color}`} style={{backgroundColor: color}} />;
    });
  };

  const getColorsContent = (): ReactElement<any>[] => {
    if (isLoading) {
      return getColorLoaders();
    }

    if (!areColorHexesSet()) {
      return getColorsEmptyState();
    }

    return getColors();
  };

  const getTextElement = (): ReactElement<any> => {
    if (isLoading || isFontLoading) {
      return <SkeletonBrick className={styles.textLoader} />;
    }

    if (!tileText) {
      return <div className={styles.emptyText} />;
    }

    return (
      <span className={`${styles.tileText} body-lg-regular -truncated-text content-body ${tileTextClasName}`} style={{fontFamily: `${fontFamily}, "Figtree", Helvetica, Arial, sans-serif`}}>
        {tileText}
      </span>
    );
  };

  useEffect(() => {
    if (!fontFamily) {
      setIsFontLoading(false);
      return;
    }

    setIsFontLoading(true);
    addFonts([fontFamily], () => {
      setIsFontLoading(false);
    });
  }, [fontFamily]);

  return (
    <div className="_position-relative">
      <ClickableDiv
        enableFocusOutline={false}
        className={`${className} flex-v-row flex-content-center ${styles.tile} ${isSelected ? styles.selected : ''} border-s-standard radius-8 ${showCursorPointer ? '_cursor-pointer' : ''}`}
        onClick={onClick}
      >
        {showImage ? <div className={`flexbox _full-width ${styles.imgContainer} flex-center spafcing-m-b-2`}>{getImageContainerContent()}</div> : null}
        <div className={`flexbox ${styles.colorsContainer} radius-4 spacing-m-b-2 ${areColorHexesSet() && !isLoading ? 'border-s-standard' : ''}`}>{getColorsContent()}</div>
        {getTextElement()}
      </ClickableDiv>
      {hasGeneratedWithAIPill ? (
        <Pill
          className={`${styles.pill} _position-absolute spacing-p-l-2 spacing-p-r-2 _cursor-pointer`}
          content={{
            type: PILL_CONTENT_TYPE.HYBRID,
            icon: 'icon-sparkle',
            iconColor: 'ai-text-dark',
            text: window.i18next.t('pmwjs_generated_with_ai'),
            textSize: TextSize.XXSMALL,
            textColor: 'ai-text-dark',
            isBold: true,
          }}
          size={PILL_SIZE.SMALL}
        />
      ) : undefined}
    </div>
  );
}
